import { useMemo } from "react";

import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import useTranslations from "@/shared/lang/useTranslations";

import type { RateProviderUsageMonth } from "../../types/rateProviderUsage.types";

interface MonthlyUsageTableProps {
  months: RateProviderUsageMonth[];
  providerCodes: string[];
  selectedMonth: string;
  formatCount: (value: number) => string;
  formatMonth: (month: string) => string;
}

/** Month column plus the total, around one column per provider. */
const FIXED_COLUMNS_WIDTH = 320;
const PROVIDER_COLUMN_WIDTH = 160;

/**
 * Requests per calendar month and provider, oldest first, so a plan overrun can be reconciled against the
 * provider's invoice and a month-over-month climb is visible before the quota runs out.
 *
 * Months are the grain because that is the billing period. The failed count rides inside the same cell rather
 * than taking a column of its own: a month is only worth opening when failures are part of what it cost, and
 * a parallel column per provider would double a matrix that already grows with every provider.
 */
export const MonthlyUsageTable = ({
  months,
  providerCodes,
  selectedMonth,
  formatCount,
  formatMonth,
}: MonthlyUsageTableProps) => {
  const { t } = useTranslations("rateProviderUsage");

  const columns = useMemo<ColocaDataTableColumn<RateProviderUsageMonth>[]>(
    () => [
      {
        id: "month",
        header: t("monthly_column_month"),
        cell: (row) => <span className="whitespace-nowrap">{formatMonth(row.month)}</span>,
      },
      ...providerCodes.map<ColocaDataTableColumn<RateProviderUsageMonth>>((code) => ({
        id: code,
        // A provider code, not a translatable label — it is data the report returned.
        header: <span className="font-mono">{code}</span>,
        headerClassName: "text-right",
        cellClassName: "text-right tabular-nums",
        cell: (row) => {
          const cell = row.totalsByProvider[code];
          if (!cell) return <span className="text-muted-foreground">{t("monthly_empty_cell")}</span>;
          return (
            <>
              {formatCount(cell.total)}
              {cell.failed > 0 ? (
                <span className="ml-1 text-xs text-destructive">
                  {t("monthly_failed_suffix", { value: formatCount(cell.failed) })}
                </span>
              ) : null}
            </>
          );
        },
      })),
      {
        id: "total",
        header: t("monthly_column_total"),
        headerClassName: "text-right",
        cellClassName: "text-right tabular-nums font-medium",
        cell: (row) => formatCount(row.total),
      },
    ],
    [providerCodes, t, formatCount, formatMonth],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={months}
      rowKey={(row) => row.month}
      minWidth={FIXED_COLUMNS_WIDTH + providerCodes.length * PROVIDER_COLUMN_WIDTH}
      rowClassName={(row) => (row.month === selectedMonth ? "bg-muted/40" : undefined)}
    />
  );
};
